/** The dev ingest edge — used for local development hostnames (`localhost`, loopback IPs). */
const DEV_INGEST_URL = 'https://metrics-dev.blufin.org/telemetry';

/** The production ingest edge — used for every non-local hostname. */
const PROD_INGEST_URL = 'https://metrics.blufin.org/telemetry';

/** The resolved beacon ingest target: the origin URL plus the canonical environment it maps to. */
export interface BeaconIngest {
  /** The ingest origin (no trailing beacon subpath), eg: `https://metrics.blufin.org/telemetry`. */
  baseUrl: string;
  /** The canonical `appEnv` for this hostname (`dev` for local, `prod` otherwise). */
  appEnv: 'dev' | 'prod';
}

export interface IResolveBeaconIngest {
  fromHostname(hostname: string): BeaconIngest;
}

/**
 * Pure resolver mapping a browser hostname to the anonymous-beacon ingest edge. Local development
 * hostnames (`localhost`, `127.*` / IPv6 loopback) route to the dev edge (`appEnv: 'dev'`); every
 * other hostname routes to the production edge (`appEnv: 'prod'`). There is deliberately no stg edge
 * on the client path — the two-site beacon ships only dev/prod (design §15.2). Exported standalone so
 * it is unit-testable without a DOM.
 */
export const ResolveBeaconIngest: IResolveBeaconIngest = {
  /**
   * Map a raw `window.location.hostname` to its ingest edge.
   * @param hostname - The browser hostname (eg: `localhost`, `127.0.0.1`, `builtbybert.com`).
   * @returns The ingest origin + canonical `appEnv`.
   */
  fromHostname(hostname) {
    const host = hostname.trim().toLowerCase();
    if (host === 'localhost' || host === '::1' || host.startsWith('127.'))
      return { appEnv: 'dev', baseUrl: DEV_INGEST_URL };
    return { appEnv: 'prod', baseUrl: PROD_INGEST_URL };
  }
};
